import { type AxiosProgressEvent } from 'axios';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';

import { getErrorMessage } from '@/api/core/axiosErrorMapper';
import { useCan } from '@/app/casl/AbilityContext';
import { RESOURCES } from '@/app/casl/resources';
import { AdminAction } from '@/app/models/AdminAction';
import { type AttachmentDto } from '@/app/models/AttachmentDto';
import { uploadFile, uploadUrl } from '@/app/pages/file-manager/core/_requests';
import DragAndDropFileUploader from '@/app/modules/table/components/DragAndDropFileUploader';
import FilesLibrary from '@/app/modules/table/components/FilesLibrary';
import FileUpload from '@/app/modules/table/components/FileUpload';
import LinkInput from '@/app/modules/table/components/LinkInput';
import { getExtensionFromFileName, getMimeTypeFromExtension } from '@/app/modules/widgets/Uploader/core/UploaderUtils';

export enum UploaderSource {
  UPLOAD = 'upload',
  URL = 'url',
  LIBRARY = 'library',
}

const defaultSources = [UploaderSource.UPLOAD, UploaderSource.URL, UploaderSource.LIBRARY];

type Props = {
  show: boolean;
  onClose: (attachments?: AttachmentDto[]) => void;
  minQuantity?: number;
  maxQuantity?: number;
  attachments?: AttachmentDto[];
  allowedExtensions?: string[];
  onlyImage?: boolean;
  temporary?: boolean;
  local?: boolean;
  sources?: UploaderSource[];
};

type FileUploadStatus = 'pending' | 'uploading' | 'success' | 'error';

type FileWithStatus = {
  name: string;
  file: File;
  status: FileUploadStatus;
  progress: number;
  size: number;
  errorMessage?: string;
};

const UploaderModal = ({
  show,
  onClose,
  minQuantity = 1,
  maxQuantity = 1,
  attachments,
  allowedExtensions,
  onlyImage,
  temporary = true,
  local = false,
  sources = defaultSources,
}: Props) => {
  const canIncludeFile = useCan(RESOURCES.GERENCIAR_ARQUIVOS, AdminAction.INCLUIR);
  const canReadFiles = useCan(RESOURCES.GERENCIAR_ARQUIVOS);
  const [activeTab, setActiveTab] = useState<'new' | 'library'>('new');
  const [files, setFiles] = useState<FileWithStatus[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [libraryAttachments, setLibraryAttachments] = useState<AttachmentDto[]>(attachments || []);
  const [pendingLibraryAttachments, setPendingLibraryAttachments] = useState<AttachmentDto[] | null>(null);
  const availableSources = new Set(sources);
  const allowUpload = availableSources.has(UploaderSource.UPLOAD);
  const allowUrl = availableSources.has(UploaderSource.URL);
  const allowLibrary = canReadFiles && availableSources.has(UploaderSource.LIBRARY);
  const showSourceTabs = allowLibrary && (allowUpload || allowUrl);

  useEffect(() => {
    if (pendingLibraryAttachments !== null) {
      setLibraryAttachments(pendingLibraryAttachments);
    }
  }, [pendingLibraryAttachments]);

  const isValidFileExtension = (file: File): boolean => {
    if (!allowedExtensions) return true;
    const fileExtension = getExtensionFromFileName(file.name);
    return allowedExtensions.includes(fileExtension);
  };

  const handleOnClose = (attachments?: AttachmentDto[]) => {
    setFiles([]);
    setLibraryAttachments([]);
    setPendingLibraryAttachments(null);
    setActiveTab('new');
    onClose(attachments);
  };

  const handleFileUpload = (newFiles: File[]) => {
    if (!canIncludeFile || isUploading) return;
    if (newFiles.length > 1) {
      setErrorMessage('Apenas um arquivo por vez é permitido.');
      return;
    }

    const validFiles = newFiles.filter(isValidFileExtension);
    if (validFiles.length !== newFiles.length) {
      setErrorMessage(
        allowedExtensions
          ? `Apenas arquivos ${allowedExtensions.join(', ')} são permitidos.`
          : 'Todos formatos de arquivos são permitidos.',
      );
      return;
    }

    setErrorMessage(null);
    if (maxQuantity === 1) {
      setLibraryAttachments([]);
      setPendingLibraryAttachments(null);
    }

    const newFilesWithStatus: FileWithStatus[] = validFiles.map((file) => ({
      name: file.name,
      file,
      status: 'pending',
      progress: 0,
      size: file.size,
    }));

    setFiles(newFilesWithStatus);
    uploadFiles(newFilesWithStatus, temporary, local);
  };

  const handleSave = () => {
    onClose(libraryAttachments);
    //clear
    setFiles([]);
    setLibraryAttachments([]);
    setPendingLibraryAttachments(null);
    setActiveTab('new');
  };

  const handleLinkSubmit = async (link: string) => {
    if (!canIncludeFile || isUploading) return;
    if (files.length >= 1 || libraryAttachments.length >= 1) {
      setErrorMessage('Apenas um arquivo por vez é permitido.');
      return;
    }

    if (!isValidFileUrl(link)) {
      setErrorMessage(
        allowedExtensions ? `URL inválida. Formatos válidos: ${allowedExtensions.join(', ')}` : 'URL inválida',
      );
      return;
    }

    setIsUploading(true);
    setErrorMessage(null);

    try {
      const { fileName, fileSize, data } = await uploadUrl({
        url: link,
        temporary,
        local,
      });

      const mimeType = getMimeTypeFromExtension(getExtensionFromFileName(fileName));

      const newFile: FileWithStatus = {
        name: fileName,
        file: new File([], fileName, { type: mimeType }),
        status: 'success',
        progress: 100,
        size: fileSize,
      };
      setLibraryAttachments((prev) => [...prev, data]);
      setFiles([newFile]);
    } catch (error) {
      setErrorMessage(getErrorMessage(error));
    } finally {
      setIsUploading(false);
    }
  };

  const submitFileUrl = canIncludeFile ? handleLinkSubmit : undefined;

  const isValidFileUrl = (url: string): boolean => {
    if (!allowedExtensions) return true;
    return allowedExtensions.some((ext) => url.toLowerCase().endsWith(ext)) && url.startsWith('http');
  };

  const handleFileCancel = (index: number) => {
    if (isUploading) return;
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleFileDelete = (index: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const updateFileStatus = (index: number, status: FileUploadStatus, progress: number, uploadErrorMessage?: string) => {
    setFiles((prevFiles) => {
      const newFiles = [...prevFiles];
      newFiles[index] = { ...newFiles[index], status, progress, errorMessage: uploadErrorMessage };
      return newFiles;
    });
  };

  const uploadFiles = async (filesToUpload: FileWithStatus[], temporary: boolean, local: boolean) => {
    if (!canIncludeFile) return;
    setIsUploading(true);
    const uploadPromises = filesToUpload.map(async (fileWithStatus, index) => {
      if (fileWithStatus.status !== 'pending') return;

      try {
        const { data } = await uploadFile(
          fileWithStatus.file,

          (progressEvent: AxiosProgressEvent) => {
            if (!progressEvent.total) return;
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            updateFileStatus(index, 'uploading', percentCompleted);
          },
          temporary,
          local,
        );

        setLibraryAttachments((previous) => {
          if (maxQuantity === 1) return [data];
          return [...previous, data];
        });

        updateFileStatus(index, 'success', 100);
      } catch (error) {
        const uploadErrorMessage = getErrorMessage(error);
        updateFileStatus(index, 'error', 0, uploadErrorMessage);
      }
    });

    await Promise.all(uploadPromises);
    setIsUploading(false);
  };

  const handleFileLibraryChange = useCallback(
    (attachments?: AttachmentDto[]) => {
      if (!canReadFiles || isUploading) return;
      setPendingLibraryAttachments(attachments || null);
    },
    [canReadFiles, isUploading],
  );

  const isSaveDisabled = useMemo(
    () => isUploading || libraryAttachments.length === 0 || libraryAttachments.length < minQuantity,
    [isUploading, libraryAttachments, minQuantity],
  );

  return (
    <Modal show={show} onHide={handleOnClose}>
      <Modal.Header closeButton className="pb-0">
        <div className="d-flex justify-content-between align-items-center w-100 flex-wrap flex-sm-nowrap gap-5 pe-sm-5">
          <Modal.Title>Envio de arquivo</Modal.Title>
          {showSourceTabs ? (
            <div
              className="nav nav-tabs nav-line-tabs d-flex flex-nowrap flex-sm-wrap"
              style={{ transform: 'translateY(1px)' }}
            >
              <div className="nav-item">
                <button
                  className={`nav-link text-nowrap ${activeTab === 'new' ? 'active' : ''}`}
                  disabled={isUploading}
                  type="button"
                  onClick={() => setActiveTab('new')}
                >
                  Novo envio
                </button>
              </div>
              <div className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'library' ? 'active' : ''}`}
                  disabled={isUploading}
                  type="button"
                  onClick={() => setActiveTab('library')}
                >
                  Biblioteca
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </Modal.Header>
      <Modal.Body className="pt-10">
        <fieldset className="tab-content border-0 m-0 p-0" disabled={isUploading}>
          <div className={clsx('tab-pane', { active: activeTab === 'new' })}>
            {allowUpload && canIncludeFile ? (
              <DragAndDropFileUploader accept={allowedExtensions} onFileUpload={handleFileUpload} />
            ) : null}
            <div className="mt-10 d-flex justify-content-center text-gray-500">
              <p>
                {allowedExtensions
                  ? `Formatos de arquivos permitidos: ${allowedExtensions.join(', ')}`
                  : 'Todos os formatos de arquivo são permitidos'}
              </p>
            </div>
            {allowUrl ? (
              <>
                <div className="separator separator-content mt-10 mb-12 text-gray-500">OU</div>
                <div className="px-0">
                  <h6 className="text-gray-800">Enviar de uma URL</h6>
                </div>
                <div className="px-0 mt-4">
                  <LinkInput onSubmit={submitFileUrl} />
                </div>
              </>
            ) : null}
            <div className="px-0 mt-4">
              {errorMessage ? <div className="alert alert-warning mt-2">{errorMessage}</div> : null}
              {files.map((fileWithStatus, index) => (
                <FileUpload
                  key={index}
                  file={{
                    name: fileWithStatus.name,
                    status: fileWithStatus.status === 'pending' ? 'uploading' : fileWithStatus.status,
                    progress: fileWithStatus.progress,
                    size: fileWithStatus.size,
                    errorMessage: fileWithStatus.errorMessage,
                  }}
                  onCancel={() => handleFileCancel(index)}
                  onDelete={() => handleFileDelete(index)}
                />
              ))}
            </div>
          </div>
          {allowLibrary ? (
            <div className={clsx('tab-pane', { active: activeTab === 'library' })}>
              <FilesLibrary
                allowedExtensions={allowedExtensions}
                initialSelectedAttachments={attachments}
                maxQuantity={maxQuantity}
                onlyImage={onlyImage}
                onChange={handleFileLibraryChange}
              />
            </div>
          ) : null}
        </fieldset>
      </Modal.Body>
      <Modal.Footer>
        <Button disabled={isUploading} variant="secondary" onClick={() => handleOnClose(attachments)}>
          Cancelar
        </Button>
        <Button disabled={isSaveDisabled} variant="primary" onClick={handleSave}>
          {isUploading ? 'Enviando...' : 'Salvar'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UploaderModal;
